import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Form, Input, Popover } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { RevxButton } from 'components/RevxButton';
import { success } from 'components/RevxMessage';
import { RevxSpin } from 'components/RevxSpin';
import {
  EmailChangePasswordTypeEnum,
  PartialAccount,
  SPECIAL_CHARACTERS,
} from 'domains/accountDomain';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { RULE_TEXT_LEN_255 } from 'utils/validators';
import './CreatePassword.less';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

export const CreatePassword = (props: { setStep: Function }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isSubmiting = useAppSelector(
    (state) => state.loading.effects.app.login
  );
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const themeData = useAppSelector((state) => state.app.themeData);
  const token = useAppSelector((state) => state.account.token);
  const tokenType = useAppSelector((state) => state.account.tokenType);
  const history = useHistory();

  const [hideStrengthBox, setHideStrengthBox] = useState(true);
  const [isLegal, setIsLegal] = useState(false);
  const [confirmPasswordIsLegal, setConfirmPasswordIsLegal] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const [form] = Form.useForm();

  const onChangePassword = async (changePasswordForm: PartialAccount) => {
    try {
      changePasswordForm.password = changePasswordForm.passwordBrowser;
      delete changePasswordForm.passwordBrowser;
      if (tokenType === 'RESET') {
        await dispatch.account.renewAccount({
          ...changePasswordForm,
          token,
        });
      } else if (tokenType === 'ACTIVATE') {
        await dispatch.account.activateAccount({
          ...changePasswordForm,
          token,
        });
      }

      success(t('login.createPassword.PasswordUpdated'));
      setTimeout(() => {
        history.push('/');
      }, 500);
    } catch (e: any) {
      // if (!e.status) {
      //   error(e.message);
      // }
    }
  };

  const onCancel = () => {
    history.push('/');
  };

  const onValuesChange = async (
    changedValues: { passwordBrowser?: string; confirmPassword?: string },
    allValues: { passwordBrowser?: string; confirmPassword?: string }
  ) => {
    setPassword(allValues.passwordBrowser?.trim() || '');
    setConfirmPassword(allValues.confirmPassword?.trim() || '');

    form.setFieldsValue({
      passwordBrowser: allValues.passwordBrowser?.trim(),
      confirmPassword: allValues.confirmPassword?.trim(),
    });

    try {
      await form.validateFields();
    } catch (error: any) {
      setConfirmPasswordIsLegal(
        !(error.errorFields || []).filter(
          (item: { name: string | string[] }) =>
            item.name.includes('password') ||
            item.name.includes('confirmPassword')
        ).length
      );
      if (error.errorFields.length) {
        setIsLegal(false);
      } else {
        setIsLegal(true);
        setConfirmPasswordIsLegal(true);
      }
    }
  };

  const checkIcon = (res: boolean) => {
    if (res) {
      return <CheckIcon className="check-icon" fill={themeData.primaryColor} />;
    } else {
      return <CloseIcon className="check-icon" fill="#ED533A" />;
    }
  };

  const checkPassword: {
    checkLength: boolean;
    checkCapital: boolean;
    checkSpecial: boolean;
    checkNumber: boolean;
    checkAllSuccess: boolean;
    checkOneSuccess: boolean;
  } = useMemo(() => {
    const checkLength = password.length >= 8;
    const checkCapital = /[A-Z]/.test(password);
    const checkSpecial = /[\^ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(password);
    const checkNumber = /[0-9]/.test(password);
    return {
      checkLength,
      checkCapital,
      checkSpecial,
      checkNumber,
      checkAllSuccess:
        checkLength && checkCapital && checkSpecial && checkNumber,
      checkOneSuccess:
        checkLength || checkCapital || checkSpecial || checkNumber,
    };
  }, [password]);

  const popoverContent = (
    <div className="createPassword-popover-content">
      {SPECIAL_CHARACTERS.map((chart) => (
        <p key={chart}>{chart}</p>
      ))}
    </div>
  );

  /* the icon after rendering the password input box */
  const renderIcon = (flag: boolean, set: (v: boolean) => void) => {
    return (
      <div
        className="password-suffix-icon"
        onClick={() => {
          set(!flag);
        }}
      >
        {flag ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </div>
    );
  };

  return (
    <div className="createPassword-form">
      <h1 className="createPassword-form-title">
        {tokenType === EmailChangePasswordTypeEnum.resetPassword
          ? t('login.createPassword.title')
          : t('login.createPassword.createTitle')}
      </h1>
      <span className="createPassword-form-subTitle">
        {t('login.createPassword.subTitle')}
      </span>
      <RevxSpin spinning={isSubmiting}>
        <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
          <Form.Item className="form-item-username">
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('login.createPassword.newPasswordRequired'),
                },
                RULE_TEXT_LEN_255,
              ]}
              label={t('login.createPassword.newPassword')}
              name="passwordBrowser"
              className={!password ? 'hide-label' : ''}
            >
              <Input
                autoComplete="off"
                className={
                  password && !passwordVisible ? 'simulation-password' : ''
                }
                placeholder={t('login.createPassword.newPassword')}
                onFocus={() => {
                  setHideStrengthBox(false);
                }}
                onBlur={() => {
                  setHideStrengthBox(true);
                }}
                suffix={renderIcon(passwordVisible, setPasswordVisible)}
              />
            </Form.Item>
          </Form.Item>
          <div className="createPassword-form-strength">
            {password && (
              <>
                <div className="createPassword-form-strength-tag"></div>
                <div
                  className={
                    checkPassword.checkOneSuccess
                      ? 'createPassword-form-strength-tag'
                      : 'createPassword-form-strength-invalidation'
                  }
                ></div>
                <div
                  className={
                    checkPassword.checkAllSuccess
                      ? 'createPassword-form-strength-tag'
                      : 'createPassword-form-strength-invalidation'
                  }
                ></div>
                <span className="createPassword-form-strength-title">
                  {t('login.createPassword.passwordStrength')}
                </span>
                <div
                  className={`createPassword-form-strength-box ${
                    (hideStrengthBox || checkPassword.checkAllSuccess) &&
                    'createPassword-form-strength-hide'
                  }`}
                >
                  <span>{t('login.createPassword.strengthList')}:</span>
                  <div className="createPassword-form-strength-rule">
                    {checkIcon(checkPassword.checkLength)}
                    {t('login.createPassword.charactersLong')}
                  </div>
                  <div className="createPassword-form-strength-rule">
                    {checkIcon(checkPassword.checkCapital)}
                    {t('login.createPassword.capitalLetter')}
                  </div>
                  <div className="createPassword-form-strength-rule">
                    {checkIcon(checkPassword.checkSpecial)}
                    {t('login.createPassword.specialCharacter')}
                    <Popover
                      content={popoverContent}
                      title={
                        <p className="createPassword-popover-title">
                          {t('login.createPassword.specialCharacterDemo')}
                        </p>
                      }
                    >
                      <ExclamationCircleOutlined className="tip-icon" />
                    </Popover>
                  </div>
                  <div className="createPassword-form-strength-rule">
                    {checkIcon(checkPassword.checkNumber)}
                    {t('login.createPassword.numberRule')}
                  </div>
                </div>
              </>
            )}
          </div>
          <Form.Item className="form-item-username form-item-confirm-password">
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('login.loginForm.passwordRequired'),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (
                      getFieldValue('passwordBrowser') === value &&
                      checkPassword.checkAllSuccess
                    ) {
                      return Promise.resolve();
                    }
                    if (!checkPassword.checkLength) {
                      return Promise.reject(
                        new Error(t('login.createPassword.charactersLong'))
                      );
                    } else if (!checkPassword.checkCapital) {
                      return Promise.reject(
                        new Error(t('login.createPassword.capitalLetter'))
                      );
                    } else if (!checkPassword.checkSpecial) {
                      return Promise.reject(
                        new Error(t('login.createPassword.specialCharacter'))
                      );
                    } else if (!checkPassword.checkNumber) {
                      return Promise.reject(
                        new Error(t('login.createPassword.numberRule'))
                      );
                    }
                    return Promise.reject(
                      new Error(t('login.createPassword.twoPasswordsMatch'))
                    );
                  },
                }),
                RULE_TEXT_LEN_255,
              ]}
              name="confirmPassword"
              label={t('login.createPassword.confirmNewPassword')}
              className={!confirmPassword ? 'hide-label' : ''}
            >
              <Input
                autoComplete="off"
                className={
                  confirmPassword && !confirmPasswordVisible
                    ? 'simulation-password'
                    : ''
                }
                placeholder={t('login.createPassword.confirmNewPassword')}
                suffix={renderIcon(
                  confirmPasswordVisible,
                  setConfirmPasswordVisible
                )}
              />
            </Form.Item>
            <div className="check-icon-box">
              {confirmPasswordIsLegal && (
                <>
                  <CheckIcon
                    className="check-icon"
                    fill={themeData.primaryColor}
                  />
                  <span>{t('login.createPassword.passwordConfirmed')}</span>
                </>
              )}
            </div>
          </Form.Item>

          <Form.Item className="form-item-button">
            <RevxButton
              size="large"
              type="primary"
              onClick={() => {
                form.validateFields().then(async (res) => {
                  await onChangePassword(res);
                });
              }}
              className={
                isLegal
                  ? 'ant-form-item-update-button'
                  : 'ant-form-item-confirm-button'
              }
            >
              {isLegal
                ? t('login.createPassword.update')
                : t('login.createPassword.submit')}
            </RevxButton>
            {isLegal && (
              <RevxButton
                size="large"
                type="link"
                className="ant-form-item-cancel-button"
                onClick={onCancel}
              >
                {t('login.createPassword.cancel')}
              </RevxButton>
            )}
          </Form.Item>
        </Form>
      </RevxSpin>
    </div>
  );
};
